import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async (currentUser) => {
    // Platform admins have no tenant — mark them with a flag so module checks always pass.
    if (!currentUser || currentUser.role === "platform_admin") {
      setSubscription(currentUser ? { is_platform: true } : null);
      setCompany(null);
      return;
    }
    try {
      const [{ data: sub }, { data: co }] = await Promise.all([
        api.get("/me/subscription"),
        api.get("/company/self"),
      ]);
      setSubscription(sub);
      setCompany(co);
    } catch (_e) {
      setSubscription(null);
      setCompany(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("wf_token");
    if (!token) {
      setUser(false);
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await fetchSubscription(data);
    } catch (_e) {
      localStorage.removeItem("wf_token");
      setUser(false);
      setSubscription(null);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, [fetchSubscription]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    const body = { password };
    if (identifier && identifier.includes("@")) {
      body.email = identifier.trim();
    } else {
      body.phone = (identifier || "").trim();
    }
    const { data } = await api.post("/auth/login", body);
    localStorage.setItem("wf_token", data.access_token);
    setUser(data.user);
    await fetchSubscription(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    localStorage.removeItem("wf_token");
    setUser(false);
    setSubscription(null);
    setCompany(null);
  };

  const refreshSubscription = useCallback(() => {
    if (user) return fetchSubscription(user);
    return Promise.resolve();
  }, [user, fetchSubscription]);

  return (
    <AuthCtx.Provider value={{ user, loading, subscription, company, login, logout, refresh, refreshSubscription }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
